import React from 'react';
import { cn } from '../../../utils/cn';
import { inrCompact } from '../constants';
import { CockpitSummary, InvoiceLabel } from '../types';

/**
 * KPI tiles — the 8 headline metrics from the HTML report. Clicking a tile sets the
 * active label filter on the table (or clears it if already active). The blind-spot
 * and "all" tiles use sentinel filter values handled by the page.
 */

export const BLIND_FILTER = '__blind__' as const;
export const ALL_FILTER = '__all__' as const;
export type TileFilter = InvoiceLabel | typeof BLIND_FILTER | typeof ALL_FILTER | null;

interface TileDef {
	key: string;
	filter: TileFilter;
	label: string;
	value: (s: CockpitSummary) => string | number;
	sub: (s: CockpitSummary) => string;
	tone: 'leak' | 'neutral' | 'chase' | 'blind' | 'proof' | 'disp' | 'ok';
	tip: string;
}

/** Left accent bar colour per tone — the only chromatic signal on an otherwise neutral tile. */
const ACCENT: Record<TileDef['tone'], string> = {
	leak: 'bg-red-500',
	neutral: 'bg-slate-400',
	chase: 'bg-amber-500',
	blind: 'bg-slate-500',
	proof: 'bg-blue-500',
	disp: 'bg-purple-500',
	ok: 'bg-emerald-500',
};

/** Ring + accent emphasis when a tile is the active filter. */
const ACTIVE_RING: Record<TileDef['tone'], string> = {
	leak: 'ring-red-300 border-red-200',
	neutral: 'ring-slate-300 border-slate-200',
	chase: 'ring-amber-300 border-amber-200',
	blind: 'ring-slate-400 border-slate-300',
	proof: 'ring-blue-300 border-blue-200',
	disp: 'ring-purple-300 border-purple-200',
	ok: 'ring-emerald-300 border-emerald-200',
};

const VALUE_TONE: Record<TileDef['tone'], string> = {
	leak: 'text-red-600',
	neutral: 'text-slate-900',
	chase: 'text-amber-600',
	blind: 'text-slate-700',
	proof: 'text-blue-600',
	disp: 'text-purple-600',
	ok: 'text-emerald-600',
};

const TILES: TileDef[] = [
	{
		key: 'leak',
		filter: 'customer_claims_paid_zoho_unpaid',
		label: 'Leak — client says paid',
		value: s => inrCompact(s.leak_customer_claims_paid.inr),
		sub: s => `${s.leak_customer_claims_paid.count} invoices · possibly already banked`,
		tone: 'leak',
		tip: 'Client emailed that they paid, but Zoho still shows unpaid. Money may already be banked, unrecorded.',
	},
	{
		key: 'out',
		filter: ALL_FILTER,
		label: 'Total outstanding',
		value: s => inrCompact(s.total_outstanding_inr),
		sub: () => 'all unpaid AR',
		tone: 'neutral',
		tip: 'Total unpaid accounts-receivable across all Zoho customer invoices.',
	},
	{
		key: 'chase',
		filter: 'chase',
		label: 'Chase — ball with client',
		value: s => inrCompact(s.chase.inr),
		sub: s => `${s.chase.count} · ball with client`,
		tone: 'chase',
		tip: "Unpaid, and the ball is in the client's court. A reminder is appropriate.",
	},
	{
		key: 'blind',
		filter: BLIND_FILTER,
		label: 'Blind spots',
		value: s => s.blind_spot_gap_a_email_not_in_zoho + s.blind_spot_gap_b_silent_invoices.count,
		sub: s =>
			`${s.blind_spot_gap_a_email_not_in_zoho} email-only · ${s.blind_spot_gap_b_silent_invoices.count} silent`,
		tone: 'blind',
		tip: 'Invoice numbers seen in email but absent from Zoho, plus unpaid Zoho invoices with zero email trail.',
	},
	{
		key: 'ib',
		filter: 'infinitybox_ack_paid_zoho_unpaid',
		label: 'We acked paid',
		value: s => inrCompact(s.leak_ib_ack_paid.inr),
		sub: s => `${s.leak_ib_ack_paid.count} invoices`,
		tone: 'leak',
		tip: 'Our own team acknowledged payment in email, yet Zoho still shows unpaid. Internal booking gap.',
	},
	{
		key: 'proof',
		filter: 'has_hard_proof',
		label: 'Hard proof',
		value: s => s.has_hard_proof.count,
		sub: s => `${inrCompact(s.has_hard_proof.inr)} backed`,
		tone: 'proof',
		tip: 'A UTR / NEFT / cheque reference (or payment screenshot) backs the paid claim.',
	},
	{
		key: 'disp',
		filter: 'disputed',
		label: 'Disputed',
		value: s => inrCompact(s.disputed.inr),
		sub: s => `${s.disputed.count} invoices`,
		tone: 'disp',
		tip: 'Thread shows a dispute or legal/quality issue. Needs resolution, not a reminder.',
	},
	{
		key: 'ok',
		filter: 'reconciled_ok',
		label: 'Reconciled',
		value: s => s.reconciled_ok.count,
		sub: () => 'paid-claim matches Zoho',
		tone: 'ok',
		tip: 'A payment claim exists AND Zoho confirms it paid. Books agree with email.',
	},
];

interface Props {
	summary: CockpitSummary;
	active: TileFilter;
	onSelect: (filter: TileFilter) => void;
}

export const KpiTiles: React.FC<Props> = ({ summary, active, onSelect }) => (
	<div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
		{TILES.map(t => {
			const isActive = active === t.filter;
			return (
				<button
					key={t.key}
					title={t.tip}
					onClick={() => onSelect(isActive ? null : t.filter)}
					className={cn(
						'group relative overflow-hidden rounded-xl border border-slate-200 bg-white pl-4 pr-3 py-3 text-left shadow-sm transition-all',
						'hover:-translate-y-0.5 hover:shadow-md',
						isActive ? cn('ring-2', ACTIVE_RING[t.tone]) : 'hover:border-slate-300'
					)}
				>
					{/* Left accent bar */}
					<span
						className={cn(
							'absolute inset-y-0 left-0 w-1 transition-all',
							ACCENT[t.tone],
							isActive ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'
						)}
					/>
					<div className='truncate text-[10.5px] font-bold uppercase tracking-[0.08em] text-slate-400'>
						{t.label}
					</div>
					<div className={cn('mt-1 text-[22px] font-extrabold leading-none tabular-nums', VALUE_TONE[t.tone])}>
						{t.value(summary)}
					</div>
					<div className='mt-1 truncate text-[11px] text-slate-400'>{t.sub(summary)}</div>
				</button>
			);
		})}
	</div>
);
